import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";
import { breadcrumbSchema, faqPageSchema, legalServiceSchema, attorneySchema } from "@/lib/schemas";
import { TRADEMARK_FAQ } from "@/lib/faq";
import { trackAuthorityPageView, trackPriorityFaqOpen } from "@/lib/analytics";
import { ChevronDown, Phone, Calendar, Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PHONE = "(972) 712-1515";
const PHONE_TEL = "9727121515";

// Priority FAQs — the eight highest-intent, most-searched trademark questions
// (curated from TRADEMARK_FAQ). Rendered above the extended list AND emitted
// as the first block in FAQPage JSON-LD to align schema with visible content.
const PRIORITY_FAQ = TRADEMARK_FAQ.slice(0, 8).map(({ q, a }) => [q, a]);
const EXTENDED_FAQ = TRADEMARK_FAQ.slice(8).map(({ q, a }) => [q, a]);
const FAQ = TRADEMARK_FAQ.map(({ q, a }) => [q, a]);

const REGISTRATION_STAGES = [
  ["Clearance Search", "We conduct a comprehensive federal, state, and common-law clearance search to identify prior registrations, pending applications, and unregistered uses that could block your mark or expose you to an opposition or infringement claim."],
  ["Filing Strategy", "We advise on the correct filing basis (use in commerce vs. intent-to-use), the classes of goods and services to file in, whether to file the word mark, the design mark, or both, and the appropriate filing options under the current USPTO fee schedule."],
  ["Application Preparation", "We prepare and file the USPTO application — precise identification of goods and services, correct filing basis, proper specimens for use-based applications, and complete owner and correspondence information."],
  ["Office Action Response", "If the USPTO examining attorney issues an Office Action, we prepare and file the response — Section 2(d) likelihood-of-confusion arguments, Section 2(e) descriptiveness responses, disclaimers, goods clarifications, and consent-agreement negotiations where appropriate."],
  ["Publication & Opposition", "After approval, the mark is published for a 30-day opposition period. We monitor publication, respond to opposition threats, and negotiate coexistence or consent agreements where an opposition is filed."],
  ["Statement of Use / Registration", "For intent-to-use applications, we prepare and file the Statement of Use with acceptable specimens once the mark is in commerce. For use-based applications, registration issues after the opposition period closes."],
  ["Maintenance & Enforcement", "We docket the Section 8 (year 5–6) and Section 8 & 9 (year 9–10) maintenance filings, monitor the trademark register for competing filings, and coordinate enforcement — cease-and-desist, TTAB proceedings, DMCA and marketplace takedowns, UDRP domain proceedings, and federal-court litigation where necessary."],
];

const PROTECTED_ASSETS = [
  ["Word marks", "The name of the brand as text, in any stylization — the strongest form of trademark protection."],
  ["Design marks", "The stylized logo, including color claims where appropriate."],
  ["Composite marks", "Word-plus-design combinations registered as a single mark."],
  ["Slogans and taglines", "Distinctive taglines that identify source — not merely describe the product."],
  ["Product configuration and trade dress", "Non-functional product shape, packaging, or store-decor elements that identify source."],
  ["Sound, color, scent, and motion marks", "Non-traditional marks that meet the source-identifier and non-functionality requirements."],
  ["Personal names and personal brands", "Founders, athletes, entertainers, and content creators registering their names as source identifiers — often through a personal-brand entity."],
  ["Domain names and social handles", "Enforcement against cybersquatting and impersonation via UDRP and platform-specific brand-protection processes."],
];

const IN_PAGE_NAV = [
  ["Why Register", "why-register"], ["What We Protect", "assets"], ["Process", "process"],
  ["Enforcement", "enforcement"], ["Coordinated Planning", "coordinated"], ["Why Lone Star Counsel", "why-lsc"],
  ["FAQs", "faqs"], ["Contact", "contact"],
];

function Bullet({ children }) {
  return (
    <li className="flex gap-3 py-3 hairline-top">
      <span className="text-brass mt-1.5 shrink-0" aria-hidden>&#9733;</span>
      <span className="text-navy font-serif leading-relaxed">{children}</span>
    </li>
  );
}

function ConsultForm() {
  const [f, setF] = useState({
    name: "", email: "", phone: "",
    mark: "", mark_type: "word",
    stage: "not-filed",
    industry: "", timeline: "",
    message: "", website: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.website) { setStatus("success"); return; }
    if (!f.name || !f.email || !f.phone) { setError("Please provide name, email, and phone."); return; }
    setStatus("loading"); setError("");
    try {
      const message = [
        f.mark && `Mark(s) to Protect: ${f.mark}`,
        `Type: ${f.mark_type}`,
        `Current Status: ${f.stage}`,
        f.industry && `Industry / Goods & Services: ${f.industry}`,
        f.timeline && `Timeline: ${f.timeline}`,
        `Landing Page: ${window.location.pathname}`,
        f.message && `\n${f.message}`,
      ].filter(Boolean).join("\n");
      await axios.post(`${API}/api/intake`, {
        full_name: f.name, email: f.email, phone: f.phone,
        practice_area: "Trademark & Brand Protection",
        stage: "growth",
        timeline: f.timeline || undefined,
        message,
        preferred_contact: "email",
        matter_class: "standard",
        role: f.mark_type,
      });
      setStatus("success");
    } catch (err) { setError(err.response?.data?.detail || "Please try again."); setStatus("error"); }
  };
  if (status === "success") return <div className="p-8 bg-ivory border border-brass/40" data-testid="tm-form-success">
    <div className="kicker">Thank you</div>
    <h3 className="mt-3 font-display text-3xl">Message received.</h3>
    <p className="mt-3 text-slate2 font-cormorant italic">A member of the firm will be in touch within two business days.</p>
  </div>;
  return <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="tm-consult-form">
    <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
    <label className="block"><div className="field-label mb-2">Name</div><input required className="field" value={f.name} onChange={set("name")} data-testid="tm-form-name" /></label>
    <label className="block"><div className="field-label mb-2">Email</div><input required type="email" className="field" value={f.email} onChange={set("email")} data-testid="tm-form-email" /></label>
    <label className="block"><div className="field-label mb-2">Phone</div><input required type="tel" className="field" value={f.phone} onChange={set("phone")} data-testid="tm-form-phone" /></label>
    <label className="block"><div className="field-label mb-2">Type of Mark</div>
      <select className="field" value={f.mark_type} onChange={set("mark_type")} data-testid="tm-form-marktype">
        <option value="word">Word mark (name)</option>
        <option value="design">Design mark (logo)</option>
        <option value="both">Both word and design</option>
        <option value="personal">Personal name / personal brand</option>
        <option value="tagline">Slogan or tagline</option>
        <option value="other">Other / not sure</option>
      </select>
    </label>
    <label className="block md:col-span-2"><div className="field-label mb-2">Mark(s) You Want to Protect</div><input className="field" value={f.mark} onChange={set("mark")} placeholder="e.g. brand name, logo, tagline" data-testid="tm-form-mark" /></label>
    <label className="block md:col-span-2"><div className="field-label mb-2">Industry, Goods, or Services</div><input className="field" value={f.industry} onChange={set("industry")} data-testid="tm-form-industry" /></label>
    <label className="block"><div className="field-label mb-2">Current Status</div>
      <select className="field" value={f.stage} onChange={set("stage")} data-testid="tm-form-stage">
        <option value="not-filed">Not yet filed</option>
        <option value="considering">Considering — need clearance search</option>
        <option value="filed">Application filed, pending</option>
        <option value="office-action">Received an Office Action</option>
        <option value="opposition">Facing an opposition</option>
        <option value="infringement">Enforcement / infringement matter</option>
        <option value="registered">Already registered — need maintenance or licensing</option>
      </select>
    </label>
    <label className="block"><div className="field-label mb-2">Timeline</div>
      <select className="field" value={f.timeline} onChange={set("timeline")} data-testid="tm-form-timeline">
        <option value="">Not urgent</option>
        <option value="30-day">Within 30 days</option>
        <option value="90-day">Within 90 days</option>
        <option value="deadline">Have an upcoming USPTO deadline</option>
      </select>
    </label>
    <label className="block md:col-span-2"><div className="field-label mb-2">Message</div><textarea rows={4} className="field" value={f.message} onChange={set("message")} data-testid="tm-form-message" /></label>
    {error && <div className="md:col-span-2 text-oxblood font-serif italic text-sm">— {error}</div>}
    <button type="submit" disabled={status === "loading"} className="md:col-span-2 btn-primary disabled:opacity-40" data-testid="tm-form-submit">
      {status === "loading" ? "Sending…" : "Schedule a Trademark Consultation"}
    </button>
  </form>;
}

function FaqItem({ q, a, i, onOpen }) {
  const [open, setOpen] = useState(i === 0);
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && onOpen) onOpen();
      return next;
    });
  };
  return <div className="hairline-top py-6" data-testid={`tm-faq-${i}`}>
    <button onClick={toggle} aria-expanded={open} className="w-full flex justify-between items-start gap-6 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass">
      <h3 className="font-display text-xl md:text-2xl text-navy leading-tight">{q}</h3>
      <ChevronDown size={22} className={`shrink-0 text-brass transition-transform mt-1 ${open ? "rotate-180" : ""}`} />
    </button>
    <div className={`${open ? "block" : "hidden"} mt-4 text-slate2 font-serif text-base leading-relaxed`}>{a}</div>
  </div>;
}

export default function TrademarkAttorneyFlagship() {
  useEffect(() => {
    trackAuthorityPageView({
      page_slug: "texas-trademark-attorney",
      page_title: "Texas Trademark Attorney",
      priority_faq_count: PRIORITY_FAQ.length,
    });
  }, []);
  return (
    <div className="bg-ivory" data-testid="trademark-flagship-page">
      <SEO
        title="Texas Trademark Attorney | Lone Star Counsel"
        description="USPTO trademark clearance, filing, prosecution, opposition, and enforcement for Texas founders, creators, athletes, and business owners. Lone Star Counsel — brand protection built for the AI-search era."
        keywords="Texas Trademark Attorney, trademark attorney Texas, USPTO trademark registration Texas, trademark lawyer Frisco Texas, trademark opposition attorney Texas, trademark infringement lawyer Texas, brand protection attorney Texas, athlete trademark attorney, creator trademark attorney, Texas trademark filing, personal brand trademark, TTAB attorney Texas"
        path="/texas-trademark-attorney/"
        schema={[
          legalServiceSchema({
            name: "Texas Trademark Attorney",
            description: "USPTO trademark clearance, filing, prosecution, opposition, enforcement, licensing, and maintenance counsel for Texas founders, creators, athletes, and privately held businesses.",
            url: "https://lonestarcounsel.com/texas-trademark-attorney/",
          }),
          attorneySchema(),
          faqPageSchema(FAQ.map(([q, a]) => ({ q, a }))),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Texas Trademark Attorney", path: "/texas-trademark-attorney/" },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="bg-ivory" data-testid="tm-hero">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="kicker">Lone Star &#9733; Counsel</div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-navy">
              Texas Trademark<br/>
              <span className="italic text-oxblood">Attorney.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate2 font-serif leading-relaxed">
              USPTO clearance, filing, prosecution, opposition, and enforcement counsel for Texas
              founders, creators, athletes, and privately held businesses building brand equity
              worth protecting. Lone Star Counsel treats a trademark as a business asset — cleared
              carefully, filed strategically, maintained on schedule, and enforced when it matters.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary" data-testid="tm-hero-primary">Schedule a Trademark Consultation</a>
              <a href={`tel:${PHONE_TEL}`} className="btn-ghost" data-testid="tm-hero-secondary">
                <Phone size={16} className="inline mr-2" />Call Lone Star Counsel
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="/img/practice-intellectual-property.jpg"
                alt="Texas trademark attorney reviewing a USPTO application — Lone Star Counsel intellectual-property practice (illustrative)"
                className="w-full h-full object-cover"
                loading="eager" fetchPriority="high"
              />
              <div className="absolute inset-0 ring-1 ring-brass/40 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* In-page navigation */}
      <nav aria-label="On this page" className="sticky top-16 z-30 bg-ivory/95 backdrop-blur border-y border-brass/30" data-testid="tm-inpage-nav">
        <ul className="max-w-[1600px] mx-auto px-6 md:px-12 py-3 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.24em] text-navy">
          {IN_PAGE_NAV.map(([label, id]) => (
            <li key={id}><a href={`#${id}`} className="hover:text-oxblood focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass py-1" data-testid={`tm-nav-${id}`}>{label}</a></li>
          ))}
        </ul>
      </nav>

      {/* INTRO */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-20">
        <p className="text-lg md:text-xl text-navy font-serif leading-relaxed">
          A trademark is not a logo. A trademark is the legal instrument that lets a founder, a creator, an athlete, or a business claim exclusive rights to a name, a logo, or a slogan as an identifier of source — and to enforce those rights against every subsequent user who tries to trade on them.
        </p>
        <p className="mt-6 text-lg md:text-xl text-navy font-serif leading-relaxed">
          In an era where a brand can go from unknown to nationally recognized in six weeks — and where AI answer engines are the new gatekeepers of consumer attention — the difference between a cleared, filed, and maintained federal registration and a common-law claim is often the difference between a defensible asset and a rebrand.
        </p>
        <p className="mt-6 text-lg md:text-xl text-navy font-serif leading-relaxed">
          Lone Star Counsel represents Texas founders, athletes, creators, franchise operators, physicians, and closely held businesses in every phase of the trademark lifecycle: clearance search, USPTO application, Office Action prosecution, opposition and cancellation proceedings before the Trademark Trial and Appeal Board, licensing and assignment, and enforcement — from cease-and-desist through federal-court litigation.
        </p>
        <div className="mt-12 p-8 bg-linen border-l-4 border-brass">
          <div className="kicker">Planning Today. Protecting Tomorrow.&reg;</div>
          <p className="mt-3 font-cormorant italic text-xl md:text-2xl text-navy leading-snug">
            The best time to protect a brand is before it needs protecting. The second best is now.
          </p>
        </div>
      </section>

      {/* WHY REGISTER */}
      <section id="why-register" className="bg-linen py-16 md:py-24" data-testid="tm-why-register">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Why Register</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Common-law rights are real. <span className="italic text-oxblood">Federal registration is stronger.</span></h2>
          <p className="mt-6 text-navy font-serif leading-relaxed">Using a mark in commerce creates common-law rights — but only within the geographic footprint where you actually use it, and only for as long as you can prove that use. Federal registration at the U.S. Patent and Trademark Office extends the right nationwide and adds a suite of statutory protections that materially strengthen every enforcement action.</p>
          <p className="mt-6 font-cormorant italic text-lg text-slate2">Federal registration provides:</p>
          <ul className="mt-4">
            <Bullet>Nationwide constructive priority as of the filing date — every later adopter is on notice.</Bullet>
            <Bullet>Presumption of validity, ownership, and exclusive right to use the mark on the identified goods and services.</Bullet>
            <Bullet>The right to use the ® symbol — a public signal of ownership that shifts the calculation of every would-be infringer.</Bullet>
            <Bullet>Access to federal court and enhanced remedies, including statutory damages for counterfeiting and — in exceptional cases — attorneys' fees.</Bullet>
            <Bullet>Recordation with U.S. Customs and Border Protection to intercept counterfeit imports at the border.</Bullet>
            <Bullet>Incontestability after five years of continuous use — dramatically raising the bar for any challenge to your registration.</Bullet>
            <Bullet>A foundational U.S. registration that can be used as the basis for international registration through the Madrid Protocol.</Bullet>
          </ul>
          <p className="mt-8 text-navy font-serif leading-relaxed">For any Texas business, creator, athlete, or founder building brand equity beyond a single city, federal registration is the baseline — not an upgrade.</p>
        </div>
      </section>

      {/* WHAT WE PROTECT */}
      <section id="assets" className="bg-ivory py-16 md:py-24" data-testid="tm-assets">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">What We Protect</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">A brand is <span className="italic text-oxblood">a portfolio of assets.</span></h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">The strongest brand protection is not a single registration but a coordinated portfolio — filed strategically, refreshed as the brand evolves, and maintained on schedule.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2" data-testid="tm-assets-list">
            {PROTECTED_ASSETS.map(([title, desc], i) => (
              <div key={i} className="py-4 hairline-top" data-testid={`tm-asset-${i}`}>
                <div className="flex items-baseline gap-3">
                  <span className="text-brass" aria-hidden>&#9733;</span>
                  <h3 className="font-display text-lg text-navy">{title}</h3>
                </div>
                <p className="mt-2 pl-6 text-sm text-slate2 leading-relaxed font-serif">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREATORS & ATHLETES CALLOUT */}
      <section className="bg-navy text-ivory py-16 md:py-24" data-testid="tm-creators">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker kicker-ivory">Creators · Athletes · Personal Brands</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl leading-[1.02]">When the founder <span className="italic text-brass">is the brand.</span></h2>
          <p className="mt-6 font-serif leading-relaxed text-ivory/90">
            An influencer's name. An athlete's signature phrase. A creator's show title. A founder's personal brand. When commercial value is attached to a person, the trademark portfolio should be built around that person — not just around the company they run today.
          </p>
          <p className="mt-6 font-serif leading-relaxed text-ivory/90">
            Lone Star Counsel structures personal-brand trademark portfolios that ordinarily include the individual's name (with the required Section 2(c) consent), the business or channel name, signature catchphrases or slogans, the logo, and — where valuable — the show, podcast, or program title. Ownership is typically held by a personal-brand entity that licenses back to the individual and to sponsors, a structure that also enables cleaner tax, estate, and asset-protection planning.
          </p>
          <p className="mt-6 font-cormorant italic text-lg text-brass">
            Related: For our creators and athletes practice, see <Link to="/practice/creators-and-athletes" className="border-b border-brass/60 pb-0.5">Creators &amp; Athletes</Link>.
          </p>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="bg-linen py-16 md:py-24" data-testid="tm-process">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Our Process</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Seven stages, <span className="italic text-oxblood">from clearance through enforcement.</span></h2>
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="tm-process-list">
            {REGISTRATION_STAGES.map(([t, d], i) => (
              <li key={i} className="bg-ivory p-8 border-t-2 border-brass" data-testid={`tm-step-${i}`}>
                <div className="text-4xl font-display text-brass">{String(i + 1).padStart(2, "0")}.</div>
                <h3 className="mt-4 font-display text-xl text-navy">{t}</h3>
                <p className="mt-3 text-sm text-slate2 font-serif leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ENFORCEMENT */}
      <section id="enforcement" className="bg-ivory py-16 md:py-24" data-testid="tm-enforcement">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Enforcement</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">A registration <span className="italic text-oxblood">is only as strong as its enforcement.</span></h2>
          <p className="mt-6 text-navy font-serif leading-relaxed">Registrations do not enforce themselves. Rights owners who tolerate infringement invite dilution — and lose leverage when they eventually need to act. Lone Star Counsel matches the enforcement tool to the infringement:</p>
          <ul className="mt-6">
            <Bullet>Cease-and-desist letters — the first tool for clear infringement by an identifiable party.</Bullet>
            <Bullet>TTAB oppositions and cancellations — for infringers who have filed their own USPTO applications.</Bullet>
            <Bullet>DMCA and marketplace brand-protection filings — for infringing listings on Amazon, Instagram, TikTok, Etsy, eBay, and other platforms.</Bullet>
            <Bullet>UDRP domain proceedings — for cybersquatters and typosquatters.</Bullet>
            <Bullet>U.S. Customs recordation — to intercept counterfeit imports at the border.</Bullet>
            <Bullet>Federal-court litigation under the Lanham Act — for injunctive relief, damages, defendant's profits, and — in exceptional cases — attorneys' fees.</Bullet>
          </ul>
        </div>
      </section>

      {/* COORDINATED */}
      <section id="coordinated" className="bg-linen py-16 md:py-24" data-testid="tm-coordinated">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Coordinated Planning</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Trademarks Never Stand <span className="italic text-oxblood">Alone.</span></h2>
          <p className="mt-6 text-navy font-serif leading-relaxed">A registered mark is a business asset with tax, estate, and liability implications. Lone Star Counsel coordinates trademark strategy with the surrounding disciplines so the owner and family receive a single, coherent plan.</p>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.24em]">
            <Link to="/practice/intellectual-property" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Intellectual Property Practice</Link>
            <Link to="/practice/creators-and-athletes" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Creators &amp; Athletes</Link>
            <Link to="/texas-asset-purchase-agreement-attorney/" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Asset Purchase Agreements</Link>
            <Link to="/texas-business-succession-planning-attorney/" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Business Succession</Link>
            <Link to="/practice" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Business Counsel</Link>
            <Link to="/texas-estate-planning-attorney/" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Estate Planning</Link>
          </div>
        </div>
      </section>

      {/* WHY LSC */}
      <section id="why-lsc" className="bg-ivory py-16 md:py-24" data-testid="tm-why">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Why Choose Us</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Why Choose <span className="italic text-oxblood">Lone Star Counsel?</span></h2>
          <ul className="mt-8">
            <Bullet>Nearly 30 years of combined legal and financial experience from a Texas attorney and CPA.</Bullet>
            <Bullet>Focused counsel for USPTO trademark prosecution, TTAB opposition and cancellation practice, and federal-court trademark enforcement.</Bullet>
            <Bullet>Deliberate representation of Texas founders, creators, athletes, franchise operators, physicians, and closely held businesses — the clients whose brands are the business.</Bullet>
            <Bullet>Coordinated planning that treats the trademark portfolio as a business asset — with tax, estate, licensing, and enforcement implications addressed under one roof.</Bullet>
            <Bullet>Attorney-CPA perspective that matters at the moment a mark is licensed, sold, contributed to an entity, or transferred as part of a broader succession or estate plan.</Bullet>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" className="bg-linen py-16 md:py-24" data-testid="tm-faq">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently Asked Questions</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Answers <span className="italic text-oxblood">before the filing.</span></h2>
          <p className="mt-4 max-w-3xl text-navy/75 font-serif leading-relaxed">
            The most common questions we receive from Texas founders, creators, athletes, and brand-holding businesses, answered directly. General information only &mdash; not legal advice on your specific matter.
          </p>
          <div className="mt-10" data-testid="tm-priority-faq">
            {PRIORITY_FAQ.map(([q, a], i) => (
              <FaqItem
                key={`priority-${i}`}
                q={q}
                a={a}
                i={`priority-${i}`}
                onOpen={() => trackPriorityFaqOpen({
                  page_slug: "texas-trademark-attorney",
                  faq_position: i + 1,
                  faq_question: q,
                })}
              />
            ))}
          </div>
          <div className="mt-16 pt-10 border-t border-brass/30" data-testid="tm-extended-faq">
            <div className="kicker">Deep-dive answers</div>
            <h3 className="mt-4 font-display text-2xl md:text-3xl text-navy leading-tight">
              Extended reference &mdash; {EXTENDED_FAQ.length} additional questions from Texas brand owners.
            </h3>
            <div className="mt-6">
              {EXTENDED_FAQ.map(([q, a], i) => <FaqItem key={i} q={q} a={a} i={i} />)}
            </div>
          </div>
        </div>
      </section>

      {/* PLAN THE NEXT CHAPTER — contextual cross-links */}
      <section id="tm-next-chapter" className="bg-ivory py-16 md:py-24 border-t border-brass/25" data-testid="tm-next-chapter">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Related counsel</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.05]">
            A trademark is a business asset. <span className="italic text-oxblood">Plan it that way.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            A registered mark is often the most valuable intangible asset in a Texas business. Lone Star Counsel coordinates trademark strategy with the surrounding disciplines so the mark, the entity that owns it, and the family that benefits from it are all planned together.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-testid="tm-next-chapter-links">
            <Link to="/practice/creators-and-athletes" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="tm-next-creators">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Creators &amp; Athletes</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                Personal-brand entities, NIL, licensing, and dedicated brand-holding structures.
              </h3>
            </Link>
            <Link to="/practice/intellectual-property" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="tm-next-ip">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Intellectual Property</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                Copyright, trade-secret, and portfolio strategy alongside the trademark filing.
              </h3>
            </Link>
            <Link to="/texas-asset-purchase-agreement-attorney/" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="tm-next-apa">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Asset Purchase Agreement</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                Transferring the mark and goodwill together when the business is bought or sold.
              </h3>
            </Link>
            <Link to="/texas-business-succession-planning-attorney/" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="tm-next-succession">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Business Succession</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                Passing the brand to the next generation without losing its legal protection.
              </h3>
            </Link>
            <Link to="/trusts/business-owner-trust-planning" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="tm-next-trust">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Business-Owner Trust</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                Coordinating trust ownership of the mark, the entity, and the estate plan.
              </h3>
            </Link>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-ivory py-16 md:py-24" data-testid="tm-contact">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="kicker">Talk With a Texas Trademark Attorney</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Schedule a Trademark <span className="italic text-oxblood">Consultation.</span></h2>
            <p className="mt-6 text-navy font-serif leading-relaxed">Whether you are naming a new business, launching a personal brand, responding to a USPTO Office Action, facing an opposition, or enforcing against an infringer, Lone Star Counsel can help you protect what you have built.</p>
            <div className="mt-8 p-6 bg-linen border-l-4 border-brass">
              <div className="kicker">Contact</div>
              <p className="mt-3 font-serif text-navy">
                <a href={`tel:${PHONE_TEL}`} className="text-oxblood font-display text-2xl">{PHONE}</a><br/>
                <span className="text-slate2 italic font-cormorant">2591 Dallas Parkway, Suite 300, Frisco, Texas 75034</span><br/>
                <span className="text-sm text-slate2">Serving founders, creators, athletes, families, and closely held businesses throughout Texas.</span>
              </p>
            </div>
          </div>
          <div><ConsultForm /></div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-navy text-ivory/70 py-10 text-xs">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 font-serif leading-relaxed">
          <strong className="text-brass tracking-widest uppercase text-[10px]">Disclaimer.</strong> The information on this page is for general informational purposes only and is not legal, tax, accounting, or financial advice. Reading this page or contacting the firm does not create an attorney-client relationship. An attorney-client relationship is established only through a written agreement signed by the client and the firm. Past results do not guarantee future outcomes. Each trademark matter depends on its specific facts and applicable law.
          <div className="max-w-3xl">
            <AttorneyReviewNote reviewed="February 2026" variant="dark" compact />
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-navy text-ivory border-t border-brass flex" data-testid="tm-sticky-cta">
        <a href={`tel:${PHONE_TEL}`} className="flex-1 py-4 text-center border-r border-brass/40 uppercase tracking-widest text-xs" data-testid="tm-sticky-call">
          <Phone size={14} className="inline mr-1" /> Call
        </a>
        <a href="#contact" className="flex-1 py-4 text-center bg-brass text-navy uppercase tracking-widest text-xs font-semibold" data-testid="tm-sticky-schedule">
          <Calendar size={14} className="inline mr-1" /> Schedule
        </a>
      </div>
    </div>
  );
}
